import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  Users, 
  LogOut,
  Menu,
  X,
  Home,
  Calendar
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import LogoutConfirmationModal from './LogoutConfirmationModal';
import JWTExpirationHandler from '../components/JWTExpirationHandler';

import Logo from '../../assets/logo.webp';

const AdminLayout = ({ onLogout }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSidebarCollapsed] = useState(true); // Collapsed by default
  const [isHovering, setIsHovering] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Updated navItems to match landing page options but without Documents and Reports
  const navItems = [
    { path: '/admin', icon: Home, label: 'Dashboard' },
    { path: '/admin/dashboard/clients', icon: Users, label: 'Client Management' },
    { path: '/admin/dashboard/sessions', icon: Calendar, label: 'Sessions' },
  ];

  // Handle hover behavior for collapsed sidebar
  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };
  
  // Adding a slight delay to hover state to prevent flickering
  useEffect(() => {
    let hoverTimer;
    if (isHovering) {
      hoverTimer = setTimeout(() => {
        // This keeps the hover state active a bit longer
        // for smoother transitions
      }, 100);
    }
    
    return () => {
      if (hoverTimer) clearTimeout(hoverTimer);
    };
  }, [isHovering]);

  const handleLogoutClick = () => {
    setIsLogoutModalOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Clear any stored session
      localStorage.removeItem('session');
      
      // Call any provided logout handler from props
      if (onLogout) {
        onLogout();
      }
      
      // Navigate to login
      navigate('/admin/login');
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setIsLogoutModalOpen(false);
    }
  };

  return (
    <JWTExpirationHandler>
      <div className="min-h-screen bg-gray-100">
        {/* Mobile Sidebar Toggle */}
        <div className="lg:hidden fixed top-4 left-4 z-50">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 bg-white rounded-lg shadow-lg"
          >
            {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Sidebar */}
        <div 
          className={`
            fixed inset-y-0 left-0 z-40 bg-teal-600 transform transition-all duration-300 ease-in-out
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            ${isSidebarCollapsed && !isHovering ? 'w-24' : 'w-64'}
            lg:translate-x-0 shadow-lg
          `}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >

          <div className="flex flex-col h-full">
            <div className={`p-4 text-center flex justify-center transition-all duration-300 ${isSidebarCollapsed && !isHovering ? 'h-16 mt-2' : ''}`}>
              <img 
                src={Logo}
                alt="Nirvana Career Counselling"
                loading="lazy" 
                className={`object-contain transition-all duration-300 ease-in-out
                  ${isSidebarCollapsed && !isHovering ? 'h-10 w-10 mt-0' : 'h-24 lg:h-28 mt-2 mb-6'}
                `}
              />
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      flex items-center px-4 py-3 rounded-lg transition-all duration-300
                      ${(location.pathname === item.path || 
                         (item.path !== '/admin' && location.pathname.startsWith(item.path))) 
                        ? 'bg-white text-teal-600' 
                        : 'text-white hover:bg-teal-700'}
                      ${isSidebarCollapsed && !isHovering ? 'justify-center' : ''}
                    `}
                    title={item.label}
                  >
                    <Icon size={20} />
                    <span 
                      className={`
                        ml-3 
                        ${isSidebarCollapsed && !isHovering 
                          ? 'w-0 opacity-0 hidden' 
                          : 'w-auto opacity-100 inline'
                        } 
                        transition-all duration-300 ease
                      `}
                    >
                      {item.label}
                    </span>
                  </Link>
                );
              })}
            </nav>

            {/* Logout Button */}
            <div className="p-4">
              <button
                onClick={handleLogoutClick}
                className={`
                  flex items-center w-full px-4 py-3 text-white hover:bg-teal-700 rounded-lg transition-all duration-300
                  ${isSidebarCollapsed && !isHovering ? 'justify-center' : ''}
                `}
                title="Logout"
              >
                <LogOut size={20} />
                <span 
                  className={`
                    ml-3 
                    ${isSidebarCollapsed && !isHovering 
                      ? 'w-0 opacity-0 hidden' 
                      : 'w-auto opacity-100 inline'
                    } 
                    transition-all duration-300 ease
                  `}
                >
                  Logout
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`
          transition-all duration-300
          ${isSidebarOpen ? (isSidebarCollapsed && !isHovering ? 'lg:ml-16' : 'lg:ml-64') : 'ml-0'}
        `}>
          {/* Top Bar */}
          <header className="bg-teal-600 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {/* Removed home button */}
                </div>
                
                <div className="flex flex-grow justify-center items-center">
                  <h1 className="text-white text-xl md:text-4xl font-bold font-lato text-center mx-auto italic">
                    Nirvana Career Counselling
                  </h1>
                </div>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <Outlet />
          </main>
        </div>

        {/* Logout Confirmation Modal */}
        <LogoutConfirmationModal 
          isOpen={isLogoutModalOpen}
          onClose={() => setIsLogoutModalOpen(false)}
          onConfirm={handleLogoutConfirm}
        />
      </div>
    </JWTExpirationHandler>
  );
};

export default AdminLayout;